import YAML, { isMap, isScalar, isSeq } from "yaml";
import * as z from "zod";
import { FormSchema, SymbolSchema, type BlaetterSlot, type Form, type Katalog, type Listenbereich, type SlotFelder, type UiDaten } from "./modell.ts";

export type ExportKontext = {
  plugin: string;
  workspace: string;
  schluessel: string;
  stand: number | null;
  katalog: Katalog;
  uiSchluessel: (id: string) => string | undefined;
};

const INDEX = z.number().int().min(0).max(53);
const DateiFelder = z.object({
  symbol: SymbolSchema,
  anzahl: z.union([z.number().int().min(1).max(64), z.string()]).optional(),
  name: z.string().optional(),
  beschreibung: z.array(z.string()).optional(),
  aktion: z.string().optional(),
  wert: z.string().optional(),
  bestaetigung: z.string().optional(),
  leuchten: z.boolean().optional(),
  bedrock_bild: z.string().optional(),
});
const DateiBlaettern = z.union([INDEX, DateiFelder.partial().extend({ slot: INDEX })]);
const DateiListe = z.object({
  name: z.string(),
  quelle: z.string(),
  slots: z.array(z.union([INDEX, z.string().regex(/^\d+-\d+$/)])),
  vorlage: DateiFelder,
  zustaende: z.record(z.string(), DateiFelder.partial()).optional(),
  blaettern: z.object({ zurueck: DateiBlaettern.optional(), info: DateiBlaettern.optional(), weiter: DateiBlaettern.optional() }).optional(),
  leer: DateiFelder.optional(),
});
export const FormatDateiSchema = z.object({
  format: z.literal(2),
  plugin: z.string(),
  workspace: z.string(),
  id: z.string(),
  stand: z.number().int().optional(),
  titel: z.string(),
  form: FormSchema,
  rahmen: z.object({ symbol: SymbolSchema }),
  quelle: z.string().optional(),
  text: z.array(z.string()).optional(),
  slots: z
    .record(z.string().regex(/^\d+$/), DateiFelder.extend({ zustaende: z.record(z.string(), DateiFelder.partial().extend({ leer: z.literal(true).optional() })).optional() }))
    .optional(),
  listen: z.array(DateiListe).optional(),
});
type DateiFelderTyp = z.infer<typeof DateiFelder>;

const FELDER = ["symbol", "anzahl", "name", "beschreibung", "aktion", "wert", "bestaetigung", "leuchten", "bedrockBild"] as const;
const BLAETTERN = ["zurueck", "info", "weiter"] as const;

export function verdichte(indizes: number[]): (number | string)[] {
  const sortiert = [...new Set(indizes)].sort((a, b) => a - b);
  const aus: (number | string)[] = [];
  let i = 0;
  while (i < sortiert.length) {
    let j = i;
    while (j + 1 < sortiert.length && sortiert[j + 1] === sortiert[j]! + 1) j++;
    aus.push(j > i ? `${sortiert[i]}-${sortiert[j]}` : sortiert[i]!);
    i = j + 1;
  }
  return aus;
}

function entfalte(eintraege: (number | string)[]): number[] {
  return eintraege.flatMap((e) => {
    if (typeof e === "number") return [e];
    const [von, bis] = e.split("-").map(Number) as [number, number];
    return Array.from({ length: bis - von + 1 }, (_, i) => von + i);
  });
}

/** Slot-Felder in Dateischreibweise; `vorgabenWeg` lässt Standardwerte weg (nur in Grundfassungen). */
function inDatei(f: Partial<SlotFelder> & { leer?: true }, k: ExportKontext, vorgabenWeg: boolean, aktionBasis?: string): Record<string, unknown> {
  const aus: Record<string, unknown> = {};
  for (const feld of FELDER) {
    let v: unknown = f[feld];
    if (v === undefined) continue;
    if (vorgabenWeg && ((feld === "anzahl" && v === 1) || (feld === "leuchten" && v === false) || (feld === "beschreibung" && (v as string[]).length === 0))) continue;
    if (feld === "wert") {
      const aktion = f.aktion ?? aktionBasis;
      if (k.katalog.aktionen.find((a) => a.schluessel === aktion)?.argument === "ui") v = k.uiSchluessel(v as string) ?? v;
    }
    aus[feld === "bedrockBild" ? "bedrock_bild" : feld] = v;
  }
  if (f.leer) aus.leer = true;
  return aus;
}

function formDatei(form: Form): Record<string, unknown> {
  return form.art === "truhe" ? { art: "truhe", reihen: form.reihen } : { art: form.art };
}

function listeDatei(l: Listenbereich, k: ExportKontext): Record<string, unknown> {
  const aus: Record<string, unknown> = { name: l.name, quelle: l.quelle, slots: verdichte(l.slots), vorlage: inDatei(l.vorlage, k, true) };
  if (l.zustaende && Object.keys(l.zustaende).length > 0) {
    aus.zustaende = Object.fromEntries(Object.entries(l.zustaende).map(([n, u]) => [n, inDatei(u, k, false, l.vorlage.aktion)]));
  }
  if (l.blaettern) {
    const b: Record<string, unknown> = {};
    for (const art of BLAETTERN) {
      const e = l.blaettern[art];
      if (!e) continue;
      const { slot, ...rest } = e;
      const felder = inDatei(rest, k, false);
      b[art] = Object.keys(felder).length === 0 ? slot : { slot, ...felder };
    }
    aus.blaettern = b;
  }
  if (l.leer) aus.leer = inDatei(l.leer, k, true);
  return aus;
}

export function exportiere(ui: UiDaten, k: ExportKontext): string {
  const kopf: Record<string, unknown> = { format: 2, plugin: k.plugin, workspace: k.workspace, id: k.schluessel };
  if (k.stand !== null) kopf.stand = k.stand;
  kopf.titel = ui.titel;
  kopf.form = formDatei(ui.form);
  kopf.rahmen = { symbol: ui.rahmen.symbol };
  if (ui.quelle) kopf.quelle = ui.quelle;
  if (ui.text.length > 0) kopf.text = ui.text;
  const indizes = Object.keys(ui.slots).map(Number).sort((a, b) => a - b);
  if (indizes.length > 0) {
    kopf.slots = new Map(
      indizes.map((i) => {
        const { zustaende, ...felder } = ui.slots[i]!;
        const s = inDatei(felder, k, true);
        if (zustaende && Object.keys(zustaende).length > 0) {
          s.zustaende = Object.fromEntries(Object.entries(zustaende).map(([n, u]) => [n, inDatei(u, k, false, felder.aktion)]));
        }
        return [i, s];
      }),
    );
  }
  if (ui.listen.length > 0) kopf.listen = ui.listen.map((l) => listeDatei(l, k));

  const doc = new YAML.Document(kopf);
  YAML.visit(doc, {
    Pair(_, pair) {
      const schluessel = isScalar(pair.key) ? pair.key.value : pair.key;
      const wert = pair.value;
      if ((schluessel === "form" || schluessel === "rahmen" || schluessel === "leer") && isMap(wert)) wert.flow = true;
      if (schluessel === "slots" && isSeq(wert)) wert.flow = true;
      if (schluessel === "blaettern" && isMap(wert)) for (const p of wert.items) if (isMap(p.value)) p.value.flow = true;
    },
  });
  return doc.toString({ lineWidth: 0 });
}

function ausDatei(f: Partial<DateiFelderTyp> & { leer?: true }): Partial<SlotFelder> & { leer?: true } {
  const { bedrock_bild, ...rest } = f;
  return bedrock_bild === undefined ? { ...rest } : { ...rest, bedrockBild: bedrock_bild };
}

export function importiere(text: string): { kopf: { plugin: string; workspace: string; id: string; stand: number | null }; ui: UiDaten } {
  const d = FormatDateiSchema.parse(YAML.parse(text));
  const ui: UiDaten = {
    titel: d.titel,
    form: d.form,
    rahmen: d.rahmen,
    text: d.text ?? [],
    slots: Object.fromEntries(
      Object.entries(d.slots ?? {}).map(([i, s]) => {
        const { zustaende, ...felder } = s;
        const slot = { ...(ausDatei(felder) as SlotFelder) } as UiDaten["slots"][string];
        if (zustaende) slot.zustaende = Object.fromEntries(Object.entries(zustaende).map(([n, u]) => [n, ausDatei(u)]));
        return [i, slot];
      }),
    ),
    listen: (d.listen ?? []).map((l) => {
      const bereich: Listenbereich = { name: l.name, quelle: l.quelle, slots: entfalte(l.slots), vorlage: ausDatei(l.vorlage) as SlotFelder };
      if (l.zustaende) bereich.zustaende = Object.fromEntries(Object.entries(l.zustaende).map(([n, u]) => [n, ausDatei(u)]));
      if (l.blaettern) {
        const b: NonNullable<Listenbereich["blaettern"]> = {};
        for (const art of BLAETTERN) {
          const e = l.blaettern[art];
          if (e === undefined) continue;
          b[art] = (typeof e === "number" ? { slot: e } : { ...ausDatei(e as Partial<DateiFelderTyp>), slot: e.slot }) as BlaetterSlot;
        }
        bereich.blaettern = b;
      }
      if (l.leer) bereich.leer = ausDatei(l.leer) as SlotFelder;
      return bereich;
    }),
  };
  if (d.quelle) ui.quelle = d.quelle;
  return { kopf: { plugin: d.plugin, workspace: d.workspace, id: d.id, stand: d.stand ?? null }, ui };
}
